/**
 * ArsenalPanel — mechanical-yard arsenal management. Shows the CURRENT hull
 * (Mk II / Mk III sprite) as a schematic with its turret ports in place. Unlock
 * any port in any order — the price rises with each unlock and is shown on every
 * locked port. Then equip / remove Twin Cannons. Locked entirely on the Mk I hull.
 */
import { useState } from "react";
import type { HudSnapshot } from "@/engine/GameState";
import { RARITY_META } from "@/game/mods/modifications";

const BASE = import.meta.env.BASE_URL;
const TURRET_URL = `${BASE}sprites/turret_basic.png`;
const CANNON_URL = `${BASE}sprites/cannon_gun.png`;
const FIGHTER_URL = `${BASE}sprites/player_fighter.png`;
const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

// Central spinal-cannon ports down the Mk IV hull's spine (matches CANNON.mounts
// on the 257×512 MTS_4 texture: ox 0, oy −63 / +44 / +154).
// Mk V per-path cannon ports (percent of hull box) — match CANNON.mountsBts/Swl.
// BTS: 2 "far back" on the superstructure shoulders + 2 on the inner-flank sockets.
const CANNON_MOUNTS_BTS = [
  { x: 24.7, y: 12.3 },
  { x: 75.3, y: 12.3 },
  { x: 35.2, y: 30.1 },
  { x: 64.8, y: 30.1 },
];
// BTS Big Cannons — the 2 largest sockets on the centre spine (match CANNON.bigMountsBts).
const BIG_CANNON_MOUNTS_BTS = [
  { x: 50, y: 48.5 },
  { x: 50, y: 60.7 },
];
const CANNON_MOUNTS_SWL = [
  { x: 50, y: 28.4 },
  { x: 50, y: 37.5 },
];
// Mk VI Swarm-lord — 8 cannons, from MK_VI_SWL_Mapping.txt (% of the 1016×1497 hull box).
const CANNON_MOUNTS_SWL_VI = [
  { x: 28.7, y: 36.1 }, { x: 71.3, y: 36.1 },
  { x: 45.2, y: 20.0 }, { x: 54.8, y: 20.0 },
  { x: 41.5, y: 5.2 }, { x: 58.5, y: 5.2 },
  { x: 50.1, y: 24.9 }, { x: 50.1, y: 32.1 },
];
// Mk VI Swarm-lord — 11 turrets, from MK_VI_SWL_Mapping.txt (% of the 1016×1497 hull box).
const MOUNTS_MKVI_SWL = [
  { x: 81.7, y: 21.9 }, { x: 18.3, y: 21.9 },
  { x: 87.5, y: 23.9 }, { x: 12.5, y: 23.9 },
  { x: 93.6, y: 25.8 }, { x: 6.4, y: 25.8 },
  { x: 67.8, y: 44.0 }, { x: 32.2, y: 44.0 },
  { x: 67.0, y: 46.9 }, { x: 33.0, y: 46.9 },
  { x: 61.9, y: 50.1 }, { x: 38.1, y: 50.1 }, // centre pair (left mirror added → 12 turrets)
];
const MOUNTS_MKVII_SWL = [
  { x: 6.4, y: 28.8 }, { x: 93.6, y: 28.8 },
  { x: 12.1, y: 27.1 }, { x: 87.9, y: 27.1 },
  { x: 17.3, y: 25.2 }, { x: 82.7, y: 25.2 },
  { x: 28.2, y: 38.4 }, { x: 71.8, y: 38.4 },
  { x: 34.1, y: 45.8 }, { x: 65.9, y: 45.8 },
  { x: 37.9, y: 49.3 }, { x: 62.1, y: 49.3 },
];
const CANNON_MOUNTS_SWL7 = [
  { x: 41.7, y: 53.9 }, { x: 58.3, y: 53.9 },
  { x: 45.4, y: 23.0 }, { x: 54.6, y: 23.0 },
  { x: 41.2, y: 9.9 }, { x: 58.8, y: 9.9 },
  { x: 27.9, y: 27.7 }, { x: 72.1, y: 27.7 },
];
const CANNON_MOUNTS = [
  { x: 50, y: 37.7 },
  { x: 50, y: 58.6 },
  { x: 50, y: 80.1 },
];

// Port positions as a % of the hull image box, matching each hull's turret mounts.
const MOUNTS_MK2 = [
  { x: 27.6, y: 11.3 },
  { x: 72.4, y: 11.3 },
  { x: 11.8, y: 35.5 },
  { x: 88.2, y: 35.5 },
];
const MOUNTS_MK3 = [
  { x: 25.9, y: 20.7 },
  { x: 74.1, y: 20.7 },
  { x: 25.9, y: 34.4 },
  { x: 74.1, y: 34.4 },
  { x: 28.3, y: 59.0 },
  { x: 71.7, y: 59.0 },
  { x: 33.7, y: 81.3 },
  { x: 66.3, y: 81.3 },
];
const MOUNTS_MK4 = [
  { x: 15.8, y: 21.8 },
  { x: 84.2, y: 21.8 },
  { x: 15.8, y: 35.2 },
  { x: 84.2, y: 35.2 },
  { x: 13.4, y: 72.3 },
  { x: 86.6, y: 72.3 },
  { x: 18.9, y: 82.8 },
  { x: 81.1, y: 82.8 },
];
// Mk V — 14 ports, matching TURRET_MOUNTS_MKV_* (BTS 237×512, SWL 402×512).
const MOUNTS_MKV_BTS = [
  { x: 10.1, y: 25.2 }, { x: 89.9, y: 25.2 },
  { x: 10.4, y: 37.7 }, { x: 89.6, y: 37.7 },
  { x: 11.4, y: 43.2 }, { x: 88.6, y: 43.2 },
  { x: 14.7, y: 48.4 }, { x: 85.3, y: 48.4 },
  { x: 17.7, y: 53.5 }, { x: 82.3, y: 53.5 },
  { x: 22.6, y: 58.8 }, { x: 77.4, y: 58.8 },
  { x: 25.9, y: 64.4 }, { x: 74.1, y: 64.4 },
];
const MOUNTS_MKV_SWL = [
  { x: 44.0, y: 22.5 }, { x: 56.0, y: 22.5 }, // centre pair
  { x: 33.5, y: 24.0 }, { x: 66.5, y: 24.0 }, // wing inner
  { x: 20.0, y: 26.0 }, { x: 80.0, y: 26.0 }, // wing mid
  { x: 8.0, y: 28.5 }, { x: 92.0, y: 28.5 }, // wing tip
  { x: 30.5, y: 44.0 }, { x: 69.5, y: 44.0 }, // flank upper
  { x: 30.0, y: 49.0 }, { x: 70.0, y: 49.0 }, // flank mid
  { x: 31.0, y: 52.5 }, { x: 69.0, y: 52.5 }, // flank lower
];
// Mk VI Battleship — 18 ports, from TURRET_MOUNTS_MKVI_BTS on the 1024×1536 hull
// (% = (170.667+ox)/341.333 for x, (256+oy)/512 for y; ox/oy in the 512 reference).
const MOUNTS_MKVI_BTS = [
  { x: 37.1, y: 17.4 }, { x: 62.9, y: 17.4 },
  { x: 35.7, y: 22.1 }, { x: 64.3, y: 22.1 },
  { x: 34.5, y: 37.9 }, { x: 65.5, y: 37.9 },
  { x: 35.1, y: 43.6 }, { x: 64.9, y: 43.6 },
  { x: 37.1, y: 48.4 }, { x: 62.9, y: 48.4 },
  { x: 41.8, y: 64.1 }, { x: 58.2, y: 64.1 },
  { x: 43.3, y: 68.2 }, { x: 56.7, y: 68.2 },
  { x: 44.7, y: 71.7 }, { x: 55.3, y: 71.7 },
  { x: 45.3, y: 76.2 }, { x: 54.7, y: 76.2 },
];
// Mk VI Battleship — 8 spinal cannons (from CANNON.mountsBtsVI).
const CANNON_MOUNTS_BTS_VI = [
  { x: 43.6, y: 22.1 }, { x: 56.4, y: 22.1 },
  { x: 45.9, y: 31.3 }, { x: 54.1, y: 31.3 },
  { x: 41.8, y: 38.3 }, { x: 58.2, y: 38.3 },
  { x: 45.3, y: 45.3 }, { x: 54.7, y: 45.3 },
];
// Mk VI Battleship — 4 Big Cannons, 2 per side (from CANNON.bigMountsBtsVI).
const BIG_CANNON_MOUNTS_BTS_VI = [
  { x: 34.8, y: 30.3 }, { x: 65.2, y: 30.3 },
  { x: 42.4, y: 56.1 }, { x: 57.6, y: 56.1 },
];

interface Props {
  snap: HudSnapshot;
  onBuyTurret: () => void;
  onUnlockSlot: (slot: number) => void;
  onEquip: (slot: number) => void;
  onUnequip: (slot: number) => void;
  onBuyCannon: () => void;
  onUnlockCannonSlot: (slot: number) => void;
  onEquipCannon: (slot: number) => void;
  onUnequipCannon: (slot: number) => void;
  onUnlockBigCannonSlot: (slot: number) => void;
  onEquipBigCannon: (slot: number) => void;
  onUnequipBigCannon: (slot: number) => void;
  onBuyFighter: (slot: number) => void;
  onDismissFighter: (slot: number) => void;
  onAssignPilot: (slot: number, crewId: string) => void;
  onUnassignPilot: (slot: number) => void;
  onClose: () => void;
}

export default function ArsenalPanel({
  snap,
  onBuyTurret,
  onUnlockSlot,
  onEquip,
  onUnequip,
  onBuyCannon,
  onUnlockCannonSlot,
  onEquipCannon,
  onUnequipCannon,
  onUnlockBigCannonSlot,
  onEquipBigCannon,
  onUnequipBigCannon,
  onBuyFighter,
  onDismissFighter,
  onAssignPilot,
  onUnassignPilot,
  onClose,
}: Props) {
  const [tab, setTab] = useState<"arsenal" | "fleet">("arsenal");
  const a = snap.arsenal;
  const ca = snap.cannonArsenal;
  const ba = snap.bigCannonArsenal;
  const fleet = snap.fleet;
  const locked = a.hullTier < 1;
  // Reflect the CURRENT hull: Mk II → MTS_2, Mk III → MTS_3.
  const shipUrl =
    a.hullTier >= 5
      ? a.mkvPath === "swarmlord"
        ? `${BASE}sprites/${a.isMkviiSwl ? "MTS_VII_SWL" : "MTS_VI_SWL"}.png`
        : `${BASE}sprites/MTS_VI_BTS.png`
      : a.hullTier >= 4
        ? `${BASE}sprites/${a.mkvPath === "swarmlord" ? "MTS_V_SWL" : "MTS_V_BTS"}.png`
        : `${BASE}sprites/MTS_${Math.min(4, a.hullTier + 1)}.png`;
  const canAffordUnlock = snap.units >= a.nextSlotCost;
  const allUnlocked = a.unlockedCount >= a.maxSlots;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Arsenal"
      >
        <div className="upg-head">
          <div className="upg-head-title">
            🔫 Arsenal ·{" "}
            {a.hullTier >= 4
              ? a.mkvPath === "battleship"
                ? "Battleship"
                : "Swarm-lord"
              : "Carrier"}{" "}
            Mk {a.isMkviiSwl ? "VII" : (ROMAN[a.hullTier] ?? "I")}
          </div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="arsenal-ribbons">
          <button
            className={`arsenal-ribbon${tab === "arsenal" ? " on" : ""}`}
            onClick={() => setTab("arsenal")}
          >
            🔫 Weapons
          </button>
          <button
            className={`arsenal-ribbon${tab === "fleet" ? " on" : ""}`}
            onClick={() => setTab("fleet")}
          >
            🚀 Fleet Management
          </button>
        </div>

        <div className="stats-body">
          {tab === "arsenal" &&
            (locked ? (
            <div className="arsenal-locked-panel">
              🔒 <b>Arsenal locked</b>
              <div>
                Refit to the <b>Mk II</b> hull at the mechanical yard to install
                hull-mounted turrets.
              </div>
            </div>
          ) : (
            <>
              <div className="svc-line">
                Twin Cannons: <b>{a.turretCount}</b>
                {ca.available && (
                  <>
                    {" "}
                    · Spinal Cannons: <b>{ca.count}</b>
                  </>
                )}
                {ba.available && (
                  <>
                    {" "}
                    · Big Cannons: <b>{ba.count}</b>
                  </>
                )}{" "}
                · ◆ {snap.units} U
              </div>
              <div className="arsenal-buy-row">
                <button
                  className="hud-btn accent"
                  disabled={snap.units < a.buyCost}
                  onClick={onBuyTurret}
                >
                  Buy Twin Cannon · {a.buyCost} U
                </button>
                {ca.available && (
                  <button
                    className="hud-btn accent"
                    disabled={snap.units < ca.buyCost}
                    onClick={onBuyCannon}
                  >
                    Buy Spinal Cannon · {ca.buyCost} U
                  </button>
                )}
              </div>

              <div
                className="arsenal-diagram"
                style={{
                  // Match the box to the CURRENT hull texture aspect so ports sit
                  // exactly on the sprite (Mk II 391×512, Mk III 203×512, Mk IV 257×512).
                  aspectRatio:
                    a.hullTier >= 5
                      ? a.mkvPath === "swarmlord"
                        ? (a.isMkviiSwl ? "1024 / 1536" : "1016 / 1497")
                        : "1024 / 1536"
                      : a.hullTier >= 4
                        ? a.mkvPath === "swarmlord"
                          ? "402 / 512"
                          : "237 / 512"
                        : a.hullTier >= 3
                          ? "257 / 512"
                          : a.hullTier >= 2
                            ? "203 / 512"
                            : "391 / 512",
                  // Bigger diagram for the crowded hulls so mounts spread out and are
                  // easy to tap (Mk VI packs 18 turrets + 8 cannons + 4 big cannons).
                  height: a.hullTier >= 5 ? 480 : a.hullTier >= 4 ? 380 : 300,
                  width: "auto",
                }}
              >
                <img className="arsenal-ship" src={shipUrl} alt="Carrier hull" />
                {(a.hullTier >= 5
                  ? a.mkvPath === "swarmlord"
                    ? (a.isMkviiSwl ? MOUNTS_MKVII_SWL : MOUNTS_MKVI_SWL)
                    : MOUNTS_MKVI_BTS
                  : a.hullTier >= 4
                    ? a.mkvPath === "swarmlord"
                      ? MOUNTS_MKV_SWL
                      : MOUNTS_MKV_BTS
                    : a.hullTier >= 3
                      ? MOUNTS_MK4
                      : a.hullTier >= 2
                        ? MOUNTS_MK3
                        : MOUNTS_MK2
                )
                  .slice(0, a.maxSlots)
                  .map((mp, i) => {
                  const unlocked = a.unlocked[i] ?? false;
                  const eq = unlocked && (a.equipped[i] ?? null) !== null;
                  const cls = eq ? "equipped" : unlocked ? "open" : "cost";
                  const disabled = eq
                    ? false
                    : unlocked
                      ? a.turretCount < 1
                      : !canAffordUnlock;
                  const title = eq
                    ? `Mount ${i + 1}: Twin Cannon — tap to remove`
                    : unlocked
                      ? `Mount ${i + 1}: empty — tap to equip`
                      : `Mount ${i + 1}: locked — unlock for ${a.nextSlotCost} U`;
                  const onClick = eq
                    ? () => onUnequip(i)
                    : unlocked
                      ? () => onEquip(i)
                      : () => onUnlockSlot(i);
                  return (
                    <button
                      key={i}
                      className={`arsenal-mount ${cls}`}
                      style={{ left: `${mp.x}%`, top: `${mp.y}%` }}
                      onClick={onClick}
                      disabled={disabled}
                      title={title}
                    >
                      {eq ? (
                        <img src={TURRET_URL} alt="" />
                      ) : unlocked ? (
                        "+"
                      ) : (
                        `${a.nextSlotCost}U`
                      )}
                    </button>
                  );
                })}

                {/* Spinal-cannon ports run down the spine (Mk IV only). */}
                {ca.available &&
                  (a.hullTier >= 5
                    ? a.mkvPath === "swarmlord"
                      ? (a.isMkviiSwl ? CANNON_MOUNTS_SWL7 : CANNON_MOUNTS_SWL_VI)
                      : CANNON_MOUNTS_BTS_VI
                    : a.mkvPath === "battleship"
                      ? CANNON_MOUNTS_BTS
                      : a.mkvPath === "swarmlord"
                        ? CANNON_MOUNTS_SWL
                        : CANNON_MOUNTS
                  )
                    .slice(0, ca.slots)
                    .map((mp, i) => {
                    const unlocked = ca.unlocked[i] ?? false;
                    const eq = unlocked && (ca.equipped[i] ?? null) !== null;
                    const cls = eq ? "equipped" : unlocked ? "open" : "cost";
                    const disabled = eq
                      ? false
                      : unlocked
                        ? ca.count < 1
                        : snap.units < ca.nextSlotCost;
                    const title = eq
                      ? `Spinal cannon ${i + 1} — tap to remove`
                      : unlocked
                        ? `Spinal slot ${i + 1}: empty — tap to equip`
                        : `Spinal slot ${i + 1}: locked — unlock for ${ca.nextSlotCost} U`;
                    const onClick = eq
                      ? () => onUnequipCannon(i)
                      : unlocked
                        ? () => onEquipCannon(i)
                        : () => onUnlockCannonSlot(i);
                    return (
                      <button
                        key={`c${i}`}
                        className={`arsenal-mount cannon ${cls}`}
                        style={{ left: `${mp.x}%`, top: `${mp.y}%` }}
                        onClick={onClick}
                        disabled={disabled}
                        title={title}
                      >
                        {eq ? (
                          <img src={CANNON_URL} alt="" />
                        ) : unlocked ? (
                          "+"
                        ) : (
                          `${ca.nextSlotCost}U`
                        )}
                      </button>
                    );
                  })}

                {/* Big-Cannon ports — Mk V: 2 on the spine; Mk VI: 4 on the flanks. */}
                {ba.available &&
                  (a.hullTier >= 5 ? BIG_CANNON_MOUNTS_BTS_VI : BIG_CANNON_MOUNTS_BTS)
                    .slice(0, ba.slots)
                    .map((mp, i) => {
                    const unlocked = ba.unlocked[i] ?? false;
                    const eq = unlocked && (ba.equipped[i] ?? null) !== null;
                    const cls = eq ? "equipped" : unlocked ? "open" : "cost";
                    const disabled = eq
                      ? false
                      : unlocked
                        ? ba.count < 1
                        : snap.units < ba.nextSlotCost;
                    const title = eq
                      ? `Big Cannon ${i + 1} — tap to remove`
                      : unlocked
                        ? `Big Cannon slot ${i + 1}: empty — tap to equip a crafted Big Cannon`
                        : `Big Cannon slot ${i + 1}: locked — unlock for ${ba.nextSlotCost} U`;
                    const onClick = eq
                      ? () => onUnequipBigCannon(i)
                      : unlocked
                        ? () => onEquipBigCannon(i)
                        : () => onUnlockBigCannonSlot(i);
                    return (
                      <button
                        key={`bc${i}`}
                        className={`arsenal-mount bigcannon ${cls}`}
                        style={{ left: `${mp.x}%`, top: `${mp.y}%` }}
                        onClick={onClick}
                        disabled={disabled}
                        title={title}
                      >
                        {eq ? (
                          <img src={CANNON_URL} alt="" />
                        ) : unlocked ? (
                          "+"
                        ) : (
                          `${ba.nextSlotCost}U`
                        )}
                      </button>
                    );
                  })}
              </div>

              <div className="arsenal-hint">
                {allUnlocked
                  ? "All mounts unlocked. Tap a port to equip or remove a Twin Cannon."
                  : `Tap any locked port to unlock it — ${a.nextSlotCost} U (the price rises each unlock). Yellow ports are armed.`}
                {ca.available &&
                  " Spinal-cannon ports (red, down the spine) unlock and arm the same way."}
                {ba.available &&
                  " Big-Cannon slots (2 large sockets on the centre spine) unlock, then equip a Big Cannon you've crafted (5 Dark Matter + 500 U)."}
              </div>
            </>
            ))}

          {tab === "fleet" &&
            (fleet.slots === 0 && fleet.droneSlots === 0 ? (
              <div className="arsenal-locked-panel">
                🔒 <b>No fighter bays yet</b>
                <div>
                  Buy <b>Fighter Bay</b> upgrades in the Mk IV branch of the upgrade
                  tree to open fleet slots.
                </div>
              </div>
            ) : (
              <>
                <div className="svc-line">
                  Fighters:{" "}
                  <b>{fleet.fighters.filter((f, i) => f && i < fleet.slots).length}</b>
                  /{fleet.slots} · deployed: <b>{fleet.deployed}</b> · ◆ {snap.units} U
                </div>
                {fleet.droneSlots > 0 && (
                  <div className="svc-line">
                    Fighter Drones: <b>{fleet.dronesDeployed}</b>/{fleet.droneSlots} · pilotless
                    swarm (buff a <b>Fighter Drone Controller</b>) · grow via <b>Drone Swarm</b>{" "}
                    upgrades
                  </div>
                )}
                <div className="fleet-grid">
                  {Array.from({ length: fleet.slots }).map((_, i) => {
                    const f = fleet.fighters[i] ?? null;
                    return (
                      <div key={i} className={`fleet-card${f ? " filled" : ""}`}>
                        <div className="fleet-card-head">Bay {i + 1}</div>
                        {f ? (
                          <>
                            <img
                              className="fleet-portrait"
                              src={
                                f.pilot
                                  ? `${BASE}portraits/${f.pilot.portrait}.png`
                                  : FIGHTER_URL
                              }
                              alt=""
                            />
                            <div
                              className={`fleet-status ${
                                f.kia ? "kia" : f.grounded ? "grounded" : "ready"
                              }`}
                            >
                              {f.kia ? "☠ K.I.A." : f.grounded ? "⚠ GROUNDED" : "✓ READY"}
                            </div>
                            {f.kia && f.pilot ? (
                              <>
                                <div
                                  className="fleet-pilot-name"
                                  style={{ color: RARITY_META[f.pilot.rarity].color }}
                                >
                                  {f.pilot.name}
                                </div>
                                <div className="fleet-kia">
                                  Killed in action — reroll at a 🍺 tavern to replace
                                </div>
                              </>
                            ) : f.pilot ? (
                              <>
                                <div
                                  className="fleet-pilot-name"
                                  style={{ color: RARITY_META[f.pilot.rarity].color }}
                                >
                                  {f.pilot.name} · T{f.pilot.tier}
                                </div>
                                <div className="fleet-buff">{f.pilot.buff}</div>
                                <button
                                  className="hud-btn ghost"
                                  onClick={() => onUnassignPilot(i)}
                                >
                                  Remove pilot
                                </button>
                              </>
                            ) : fleet.availablePilots.length > 0 ? (
                              <select
                                className="fleet-select"
                                defaultValue=""
                                onChange={(e) => {
                                  if (e.target.value) onAssignPilot(i, e.target.value);
                                }}
                              >
                                <option value="" disabled>
                                  Assign pilot…
                                </option>
                                {fleet.availablePilots.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name} ({RARITY_META[p.rarity].label} T{p.tier})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="fleet-pilot">
                                No pilot — recruit at a 🍺 Space Tavern
                              </div>
                            )}
                            <button
                              className="hud-btn ghost"
                              onClick={() => onDismissFighter(i)}
                            >
                              Scrap
                            </button>
                          </>
                        ) : (
                          <button
                            className="hud-btn accent"
                            disabled={snap.units < fleet.buyCost}
                            onClick={() => onBuyFighter(i)}
                          >
                            Buy Fighter · {fleet.buyCost} U
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="arsenal-hint">
                  Equipped fighters launch and fight during combat (same class as
                  enemy fighters). A pilot's buff raises their speed, HP, shield and
                  fire rate — recruit pilots at a 🍺 Space Tavern.
                </div>
              </>
            ))}
        </div>
      </div>
    </div>
  );
}
